import React, { useState } from "react";
import { PageLayout } from "components/layout/PageLayout";
import { Button } from "components/ui/Button";
import { FadeIn } from "components/shared/FadeIn";
import { greeting, contactPageData, socialMediaLinks } from "portfolio";
import type { ThemeWithOptionalSetterProps } from "types";

const Contact: React.FC<ThemeWithOptionalSetterProps> = (props) => {
  const theme = props.theme;
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <PageLayout theme={theme} setTheme={props.setTheme}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 py-12 md:py-20">
        {/* Left: Dramatic typography */}
        <FadeIn duration={800} distance="24px">
          <div className="space-y-8">
            <div>
              <p className="font-mono text-xs tracking-[0.12em] uppercase text-text-muted mb-4">
                {contactPageData.contactSection.title}
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight text-text-primary leading-tight">
                Let&apos;s build
                <br />
                <span className="text-accent">something great</span>
              </h1>
            </div>

            <p className="text-base leading-relaxed text-text-secondary max-w-md">
              {contactPageData.contactSection.description}
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="text-sm font-mono text-text-secondary">
                  Open to full-time opportunities
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="text-sm font-mono text-text-secondary">
                  Architecture consulting
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="text-sm font-mono text-text-secondary">
                  AI & platform conversations
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-xs font-mono text-text-muted uppercase tracking-wider mb-2">
                Email
              </p>
              <a
                href={`mailto:${greeting.mail}`}
                className="text-lg font-mono text-accent hover:underline"
              >
                {greeting.mail}
              </a>
            </div>

            <div className="flex gap-6">
              {socialMediaLinks.github && (
                <a
                  href={socialMediaLinks.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs text-text-muted hover:text-accent transition-colors link-underline"
                >
                  GitHub
                </a>
              )}
              {socialMediaLinks.linkedin && (
                <a
                  href={socialMediaLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs text-text-muted hover:text-accent transition-colors link-underline"
                >
                  LinkedIn
                </a>
              )}
              {socialMediaLinks.x && (
                <a
                  href={socialMediaLinks.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs text-text-muted hover:text-accent transition-colors link-underline"
                >
                  X / Twitter
                </a>
              )}
            </div>
          </div>
        </FadeIn>

        {/* Right: Form */}
        <FadeIn duration={800} distance="24px">
          {submitted ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-display font-bold text-text-primary">
                Message sent
              </h3>
              <p className="text-sm text-text-secondary">
                I&apos;ll get back to you within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-bg-elevated border border-border rounded-md text-text-primary font-mono text-sm placeholder:text-text-muted focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/20 transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-bg-elevated border border-border rounded-md text-text-primary font-mono text-sm placeholder:text-text-muted focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/20 transition-colors"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
                  Message
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-bg-elevated border border-border rounded-md text-text-primary font-mono text-sm placeholder:text-text-muted focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/20 transition-colors resize-none"
                  placeholder="Describe your project, opportunity, or just say hi..."
                />
              </div>

              <Button variant="primary" size="lg" className="w-full">
                Send Message
              </Button>
            </form>
          )}
        </FadeIn>
      </div>
    </PageLayout>
  );
};

export default Contact;
